import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Divider,
  Icon,
  HStack,
  Badge,
  Button,
  Link,
} from '@chakra-ui/react';
import { FiShield, FiInfo, FiSettings } from 'react-icons/fi';
import { BiCookie } from 'react-icons/bi';

const Section = ({ icon, title, children }) => (
  <Box 
    w="full" 
    bg={useColorModeValue('white', 'gray.800')} 
    p={6} 
    borderRadius="lg" 
    shadow="md"
    transition="all 0.3s"
    _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
  >
    <HStack spacing={4} mb={4}>
      <Icon as={icon} w={6} h={6} color="brand.500" />
      <Heading size="md">{title}</Heading>
    </HStack>
    {children}
  </Box>
);

const CookiePolicy = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const tableBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const cookies = [
    { 
      name: 'Essential Cookies',
      purpose: 'Required for core site functionality',
      duration: 'Session',
      type: 'Necessary'
    },
    { 
      name: 'Authentication Cookies',
      purpose: 'Maintain your logged-in status',
      duration: '30 days',
      type: 'Necessary'
    },
    { 
      name: 'Preference Cookies',
      purpose: 'Remember your settings and choices',
      duration: '1 year',
      type: 'Functional'
    },
    { 
      name: 'Analytics Cookies',
      purpose: 'Help us understand how you use our service',
      duration: '2 years',
      type: 'Analytics'
    },
  ];

  return (
    <Box bg={bgColor} minH="100vh" py={12}>
      <Container maxW="container.md">
        <VStack spacing={8} align="start">
          <VStack align="start" spacing={3} w="full">
            <Badge colorScheme="brand" fontSize="sm" px={3} py={1}>
              Cookie Policy
            </Badge>
            <Heading size="xl" color={useColorModeValue('gray.800', 'white')}>
              How We Use Cookies
            </Heading>
            <Text color="gray.500">
              Last updated: {new Date().toLocaleDateString()}
            </Text>
            <Divider my={4} borderColor={borderColor} />
          </VStack>

          <VStack spacing={6} w="full">
            <Section icon={FiInfo} title="What Are Cookies">
              <Text lineHeight="tall">
                Cookies are small text files that are placed on your computer by websites that you visit.
                They are widely used to make websites work more efficiently and provide a better user experience.
              </Text>
              <Box 
                mt={4} 
                p={4} 
                bg={useColorModeValue('blue.50', 'blue.900')} 
                color={useColorModeValue('blue.800', 'blue.200')} 
                borderRadius="md"
              >
                <Text fontWeight="medium">
                  This website uses cookies to ensure you get the best experience on our platform.
                </Text>
              </Box>
            </Section>

            <Section icon={BiCookie} title="Cookies We Use">
              <Box overflowX="auto" w="full">
                <Table variant="simple" bg={tableBg} borderRadius="lg" overflow="hidden">
                  <Thead bg={useColorModeValue('gray.50', 'gray.700')}>
                    <Tr>
                      <Th>Type</Th>
                      <Th>Purpose</Th>
                      <Th>Duration</Th>
                      <Th>Category</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {cookies.map((cookie, index) => (
                      <Tr key={index}>
                        <Td fontWeight="medium">{cookie.name}</Td>
                        <Td>{cookie.purpose}</Td>
                        <Td>{cookie.duration}</Td>
                        <Td>
                          <Badge
                            colorScheme={
                              cookie.type === 'Necessary' ? 'red' :
                              cookie.type === 'Functional' ? 'green' :
                              'blue'
                            }
                          >
                            {cookie.type}
                          </Badge>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Section>

            <Section icon={FiSettings} title="Managing Cookies">
              <Text lineHeight="tall" mb={4}>
                You can control and/or delete cookies as you wish. You can delete all cookies that are
                already on your computer and you can set most browsers to prevent them from being placed.
              </Text>
              <Link color="brand.500" href="#" textDecoration="underline">
                Learn more about cookie management
              </Link>
            </Section>
          </VStack>

          <Button 
            colorScheme="brand" 
            size="lg" 
            w="full"
            mt={4}
          >
            Accept All Cookies
          </Button>
        </VStack>
      </Container>
    </Box>
  );
};

export default CookiePolicy; 